import { addMonths, effDay, formatYm, formatYmd, monthOf, parseYm, parseYmd, type Ym, type Ymd } from './month';

/**
 * Cálculo puro de em qual fatura uma compra cai — fonte única compartilhada
 * entre o motor do backend (escrita) e o preview do modal (client).
 *
 * Convenção Mobills (ver CLAUDE.md § Cartões):
 * - `invoice_month` = mês do VENCIMENTO ('YYYY-MM').
 * - Compra no dia do fechamento ou depois cai na PRÓXIMA fatura.
 * - Se `dueDay > closingDay`, a fatura vence no mesmo mês do fechamento;
 *   caso contrário, vence no mês seguinte ao fechamento.
 */

export interface CardCycle {
  closingDay: number; // 1..31
  dueDay: number; // 1..31
}

/** Mês do fechamento ('YYYY-MM') a que a compra pertence. */
export function closingMonthFor(card: CardCycle, purchaseDate: Ymd): Ym {
  const { year, month, day } = parseYmd(purchaseDate);
  const closingEff = effDay(year, month, card.closingDay);
  const thisMonth = formatYm(year, month);
  // No dia do fechamento ou depois → fecha no ciclo do mês seguinte.
  return day >= closingEff ? addMonths(thisMonth, 1) : thisMonth;
}

/** Mês do vencimento ('YYYY-MM') = invoice_month da compra. */
export function invoiceMonthFor(card: CardCycle, purchaseDate: Ymd): Ym {
  const closeMonth = closingMonthFor(card, purchaseDate);
  return card.dueDay > card.closingDay ? closeMonth : addMonths(closeMonth, 1);
}

/** Janela de compras da fatura de `invoiceMonth`: [opensOn, closesOn) e a data de vencimento. */
export function invoicePeriod(
  card: CardCycle,
  invoiceMonth: Ym,
): { opensOn: Ymd; closesOn: Ymd; dueDate: Ymd } {
  // Mês do fechamento a partir do mês de vencimento (inverso de invoiceMonthFor).
  const closeMonth = card.dueDay > card.closingDay ? invoiceMonth : addMonths(invoiceMonth, -1);
  const { year: cy, month: cm } = parseYm(closeMonth);
  const closesOn = formatYmd(cy, cm, effDay(cy, cm, card.closingDay));

  // A janela abre no fechamento do mês anterior (inclusive) e fecha em closesOn (exclusivo).
  const prevClose = addMonths(closeMonth, -1);
  const { year: py, month: pm } = parseYm(prevClose);
  const opensOn = formatYmd(py, pm, effDay(py, pm, card.closingDay));

  const { year: dy, month: dm } = parseYm(invoiceMonth);
  const dueDate = formatYmd(dy, dm, effDay(dy, dm, card.dueDay));

  return { opensOn, closesOn, dueDate };
}

/** Conferência: uma compra pertence à janela [opensOn, closesOn). */
export function purchaseInInvoice(card: CardCycle, invoiceMonth: Ym, purchaseDate: Ymd): boolean {
  return invoiceMonthFor(card, purchaseDate) === invoiceMonth && monthOf(purchaseDate) !== '';
}
